import { find, isEmpty } from 'lodash-es';
import hardtack from 'hardtack';
import { SETTINGS, SITE, ONBOARDING } from '@hotjar/webapp/constants/routes';

import type { CurrentUser } from '@hotjar/user';
import * as SegmentTracking from '@hotjar/webapp/domains/common/tracking/segment';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { getCurrentUser, CURRENT_USER_QUERY_KEY } from '@hotjar/user';
import { sentry, segment } from '@hotjar/tracking';
import { mapOwnedAccountModelToDto } from '@hotjar/account';
import { mapSiteDtoToModel, nonReactSetPreferredCurrentSiteId } from '@hotjar/site';
import type { SiteDTO } from '@hotjar/site';
import {
  getUserResources,
  setCurrentSiteInLocalStorage,
  USER_RESOURCES_QUERY_KEY,
  userResourcesModelToDto,
} from '@hotjar/user-resources';
import type { UserResourcesDto } from '@hotjar/user-resources';
import type { Organization as OrganizationModel } from '@hotjar/organization';
import type { ExtendedCurrentUser, UserData } from './model';

/**
 * Future person: this function has been re-exported by `@hotjar/subscription-management` and `@hotjar/user`, if
 * you extract this out of webapp into a standalone util please inform
 * `@hotjar/monetization-platform` or `@hotjar/billing` and `@hotjar/tame` FE, so they are aware.
 */
export function getLoggedIn(): boolean {
  return hardtack.get('REF') !== undefined;
}

export const loadUserDataFromStorage = (): ExtendedCurrentUser => {
  const userData = (LocalStorage.get('userData') || {}) as ExtendedCurrentUser;

  // The user might have a pre-versioning copy of userData stored locally.
  // In this case set the version to 0 so it gets updated.
  userData.version = userData.version || 0;

  if (userData.userId) {
    SegmentTracking.identify(userData as ExtendedCurrentUser);
  }

  return userData;
};

export const saveUserData = (userData: UserData | Record<string, unknown>): void => {
  LocalStorage.set('userData', userData);
};

export const clearUserData = (): void => {
  saveUserData({});
};

/* FURTHER are functions migrated from UserData service,
 but it was tested and used in register flow only and needs refactoring */

export const identifyUserData = (userData: ExtendedCurrentUser): void => {
  // Since by default the userData is empty, only identify if we have
  // a userId
  if (userData.userId) {
    segment.identify(userData);
  }
};

const isAtPermissionRequiredPage = () => {
  const noPermissionUrls = [
    '/onboarding',
    '/register',
    '/login',
    '/login/mfa',
    '/setup',
    SETTINGS.ACCOUNT.path,
    SITE.LIST.path,
    ONBOARDING.JOIN_TEAM.path,
    '',
  ];
  return noPermissionUrls.every((url) => window.location.pathname !== url);
};

const renameUserProperties = (user: CurrentUser): ExtendedCurrentUser => {
  return {
    ...user,
    userCreated: user.created,
    userEmail: user.email,
    userEmailBlocked: user.emailBlocked,
    userId: user.id,
    userName: user.name,
    userPictureUrl: user.avatar,
    userRole: user.role,
  };
};

const setFromServerResponse = (response: ExtendedCurrentUser) => {
  const updatedUser = {
    ...response,
    // getUserResources returns an account model,
    // we change this to dto since this what is expected across angular ctrl's & services
    accounts: response.accounts?.map((account) => mapOwnedAccountModelToDto(account)),
    accountId: response.accounts && response.accounts[0].id,
    countryCode: response.country || undefined,
    version: window.settings.version,
  };
  saveUserData(updatedUser);

  return updatedUser;
};

const fetchResourcesFromServer = (userData: ExtendedCurrentUser): Promise<UserResourcesDto> => {
  const { userId } = userData;

  return queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY).then(() =>
    queryClient
      .fetchQuery(
        USER_RESOURCES_QUERY_KEY,
        () => {
          return getUserResources(userId as number);
        },
        {
          staleTime: 0,
          cacheTime: 60 * 60 * 1000,
        },
      )
      .then((response) => {
        return userResourcesModelToDto(response);
      }),
  );
};

const refreshResourcesFromServer = (userData: ExtendedCurrentUser): Promise<UserResourcesDto> => {
  return fetchResourcesFromServer(userData).then((resources) => {
    const userResources = resources;

    let { currentSite } = userData;
    const { sites } = userResources;

    // Either use the updated object for the current site with the
    // same id.
    //
    // Else, if this does not exist (e.g. has been deleted or the
    // user no longer has access to it), set to `undefined` if the
    // sites list is empty, otherwise use the first site in the list.
    const updatedCurrentSite: SiteDTO | undefined = currentSite
      ? find(sites, { id: currentSite.id })
      : undefined;

    if (updatedCurrentSite) {
      currentSite = updatedCurrentSite as unknown as SiteDTO;
    } else {
      const isPermissionRequired = isAtPermissionRequiredPage();
      currentSite = isPermissionRequired || isEmpty(sites) ? undefined : sites[0];
    }
    nonReactSetPreferredCurrentSiteId(currentSite?.id);
    if (currentSite) {
      setCurrentSiteInLocalStorage(
        mapSiteDtoToModel(currentSite, resources.organizations as unknown as OrganizationModel[]),
      );
    } else {
      setCurrentSiteInLocalStorage(undefined);
    }

    userResources.sites.forEach((siteObj) => {
      const site = siteObj;
      site.name = site.name || 'My site';
    });

    return userResources;
  });
};

export const refreshFromServer = (): Promise<UserResourcesDto> => {
  // We use queryClient from react-query so that the request is cached for use by the react hook
  // useCurrentUser
  return queryClient
    .fetchQuery(CURRENT_USER_QUERY_KEY, getCurrentUser, { staleTime: 0, cacheTime: 60 * 60 * 1000 })
    .then((user) => {
      if (!user) {
        throw new Error('Unauthorized');
      }

      const mappedUser = renameUserProperties(user);
      const updatedUser = setFromServerResponse(mappedUser);

      segment.setAnonymizedUserId(mappedUser.currentIp);
      segment.setCountryCode(updatedUser.countryCode);
      sentry.setSentryIdentity(mappedUser.userId);
      identifyUserData(mappedUser);

      return refreshResourcesFromServer(mappedUser);
    });
};
